import React, { useState, useEffect, memo } from 'react';
import { API_BASE_URL } from '../../../utils/apiConfig';

/**
 * AlertsList - Widget "Alertes à traiter" pour Planificateur
 */
const AlertsList = memo(({ token, onNavigate }) => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAlerts = async () => {
      if (!token) return;

      setLoading(true);
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/dashboard/alerts`,
          {
            headers: { 'Authorization': `Bearer ${token}` }
          }
        );

        if (!response.ok) throw new Error('Erreur de chargement');

        const data = await response.json();
        setAlerts(data.success ? data.data : []);
      } catch (err) {
        console.error('Erreur fetch alerts:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAlerts();
  }, [token]);

  const getAlertInfo = (alertType) => {
    const alertTypes = {
      'no_team_leader': {
        icon: '👤',
        label: 'Chef d\'équipe non assigné',
        description: 'Aucun responsable désigné pour superviser cette mission',
        color: '#dc3545'
      },
      'no_participants': {
        icon: '👥',
        label: 'Aucun participant',
        description: 'Personne n\'est affecté à cette prestation',
        color: '#dc3545'
      },
      'incomplete_staff': {
        icon: '👥',
        label: 'Effectif incomplet',
        description: 'L\'équipe n\'est pas au complet',
        color: '#ffc107'
      },
      'no_location': {
        icon: '📍',
        label: 'Lieu non défini',
        description: 'Aucun lieu de mise ni client défini',
        color: '#fd7e14'
      },
      'conflict': {
        icon: '⚠️',
        label: 'Conflit de planning',
        description: 'Un participant est déjà affecté ailleurs',
        color: '#6c757d'
      }
    };
    return alertTypes[alertType] || {
      icon: '❓',
      label: 'Alerte inconnue',
      description: `Type: ${alertType}`,
      color: '#6c757d'
    };
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <h4 style={styles.title}>Alertes à traiter</h4>
        <div style={styles.loading}>Chargement...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <h4 style={styles.title}>Alertes à traiter</h4>
        <div style={styles.error}>{error}</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h4 style={styles.title}>
          Alertes à traiter
          {alerts.length > 0 && (
            <span style={styles.count}>({alerts.length})</span>
          )}
        </h4>
        <button
          style={styles.viewButton}
          onClick={() => onNavigate && onNavigate('planning')}
        >
          Voir planning
        </button>
      </div>

      {alerts.length === 0 ? (
        <div style={styles.empty}>
          <span style={styles.emptyIcon}>✅</span>
          <p style={styles.emptyText}>Aucune alerte à traiter</p>
        </div>
      ) : (
        <div style={styles.list}>
          {alerts.slice(0, 5).map((alert) => {
            const alertInfo = getAlertInfo(alert.alert_type);
            return (
              <div
                key={alert.id}
                style={{ ...styles.alertItem, borderLeftColor: alertInfo.color }}
              >
                <span style={styles.alertIcon}>{alertInfo.icon}</span>
                <div style={styles.alertContent}>
                  <div style={styles.alertHeader}>
                    <span style={styles.prestationNumber}>{alert.prestation_number}</span>
                    <span style={styles.alertDate}>{formatDate(alert.start_date)}</span>
                  </div>
                  <div style={styles.alertLabel}>
                    {alertInfo.label}
                    {alert.alert_type === 'incomplete_staff' && (
                      <span style={styles.staffCount}>
                        ({alert.participant_count}/{alert.target_participants})
                      </span>
                    )}
                    {alert.alert_type === 'conflict' && alert.conflict_user_name && (
                      <span style={styles.conflictUser}>
                        : {alert.conflict_user_name}
                      </span>
                    )}
                  </div>
                  {alert.alert_type === 'conflict' && alert.client_name && alert.client_name_2 ? (
                    <div style={styles.clientName}>
                      {alert.client_name} ↔ {alert.client_name_2}
                    </div>
                  ) : alert.client_name && (
                    <div style={styles.clientName}>{alert.client_name}</div>
                  )}
                </div>
                <button
                  style={styles.openButton}
                  onClick={() => onNavigate && onNavigate('planning')}
                >
                  Ouvrir
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
});

AlertsList.displayName = 'AlertsList';

const styles = {
  container: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px'
  },
  title: {
    margin: 0,
    fontSize: '1.1rem',
    fontWeight: '600',
    color: '#1a1a2e',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  count: {
    backgroundColor: '#dc3545',
    color: 'white',
    padding: '2px 8px',
    borderRadius: '12px',
    fontSize: '0.85rem',
    fontWeight: '500'
  },
  viewButton: {
    background: 'none',
    border: 'none',
    color: '#6366f1',
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: '500'
  },
  loading: {
    textAlign: 'center',
    padding: '30px',
    color: '#6c757d'
  },
  error: {
    padding: '20px',
    backgroundColor: '#fff5f5',
    borderRadius: '8px',
    color: '#dc3545',
    textAlign: 'center'
  },
  empty: {
    textAlign: 'center',
    padding: '30px'
  },
  emptyIcon: {
    fontSize: '2rem',
    display: 'block',
    marginBottom: '8px'
  },
  emptyText: {
    margin: 0,
    color: '#6c757d'
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  alertItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    borderLeft: '4px solid'
  },
  alertIcon: {
    fontSize: '1.2rem'
  },
  alertContent: {
    flex: 1,
    minWidth: 0
  },
  alertHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '4px'
  },
  prestationNumber: {
    fontWeight: '600',
    fontSize: '0.9rem',
    color: '#1a1a2e'
  },
  alertDate: {
    fontSize: '0.8rem',
    color: '#6c757d'
  },
  alertLabel: {
    fontSize: '0.85rem',
    color: '#495057'
  },
  staffCount: {
    marginLeft: '4px',
    color: '#dc3545',
    fontWeight: '500'
  },
  clientName: {
    fontSize: '0.8rem',
    color: '#6c757d',
    marginTop: '2px'
  },
  conflictUser: {
    fontWeight: '600',
    color: '#dc3545'
  },
  alertDescription: {
    fontSize: '0.75rem',
    color: '#868e96',
    marginTop: '2px',
    fontStyle: 'italic'
  },
  openButton: {
    padding: '6px 12px',
    backgroundColor: '#6366f1',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: '500',
    whiteSpace: 'nowrap'
  }
};

export default AlertsList;
